import { camelCase } from 'lodash';
import { toISODateFormat } from 'works/legacy/lib/date-helper';
import {
  RATE_FIELDS,
  SHIFTS,
  WEEK_DAYS,
  CORE_DAY_INPUT_KEYS,
  FLOAT_DAY_INPUT_KEYS,
  CORE_NIGHT_INPUT_KEYS,
  FLOAT_NIGHT_INPUT_KEYS,
  DYNAMIC_RATES_INPUT_KEYS,
} from '@scheduling/services';
import {
  UnitRateType,
  SimplifiedWorkforceGroupType,
} from '@scheduling/graphql';
import {
  SelectOrganizationUnitType,
  VariableRatesType,
  WeekDaysRates,
} from '@institutions/graphql';

function buildShiftRateKey(shift: string) {
  return `${camelCase(shift)}IncentiveRates`;
}

export function getTransformedData({
  selectedWorkforceGroups,
  effectiveDate,
  endDate,
  selectedOrgsUnits,
  ratesValues,
  pricingMode,
}: {
  selectedWorkforceGroups: SimplifiedWorkforceGroupType[];
  effectiveDate: string;
  endDate: string;
  selectedOrgsUnits: SelectOrganizationUnitType[];
  ratesValues: UnitRateType;
  pricingMode: 'DYNAMIC' | 'STATIC';
}) {
  const isStatic = pricingMode === 'STATIC';

  // TODO: Remove all this logic changing the mutation to receive the same fields as in workforce_group_unit_rates.
  function getAllVariableUnitRates() {
    function buildWeekdayKeyValue(
      days: WeekDaysRates,
      key: string,
      index: number
    ) {
      days[WEEK_DAYS[index] as keyof WeekDaysRates] =
        (ratesValues[key as keyof UnitRateType] as number) ?? 0;

      return days;
    }

    function buildShiftKeyValue(
      shifts: VariableRatesType,
      fields: string[],
      index: number
    ) {
      shifts[buildShiftRateKey(SHIFTS[index]) as keyof VariableRatesType] =
        fields.reduce(buildWeekdayKeyValue, {} as WeekDaysRates);
      return shifts;
    }

    return RATE_FIELDS.reduce(buildShiftKeyValue, {} as VariableRatesType);
  }

  function getAllDynamicRates() {
    function buildDefaultTypeKeyValue(
      types: Record<string, number>,
      key: string
    ) {
      types[key] = ratesValues[key as keyof UnitRateType] as number;
      return types;
    }

    return DYNAMIC_RATES_INPUT_KEYS.reduce(buildDefaultTypeKeyValue, {}) as {
      priceRange: number;
      incentiveIncrement: number;
    };
  }

  const variableRates = getAllVariableUnitRates();

  return {
    workforceGroupIds: selectedWorkforceGroups.map((wfg) => wfg.id),
    effectiveDate: effectiveDate && toISODateFormat(effectiveDate),
    endDate: (endDate !== '' && toISODateFormat(endDate)) || null,
    organizationUnitIds: selectedOrgsUnits.map((unit) => unit.id),
    rates: {
      ...(isStatic
        ? {
            priceRange: 0,
            incentiveIncrement: 0,
            displayAsLumpSum: ratesValues.displayAsLumpSum ?? true,
            staticPricingTiers: (ratesValues.staticPricingTiers || []).map(
              (tier) => ({
                label: tier.label,
                fillRateThreshold: tier.fillRateThreshold,
                payRateCents: Math.round(tier.payRate * 100),
              })
            ),
          }
        : getAllDynamicRates()),
      variableRates,
    },
  };
}

export function filterFormData(
  formRawData: UnitRateType,
  pricingMode: 'DYNAMIC' | 'STATIC'
) {
  const formFilteredData = {} as Record<string, unknown>;

  function assignValuesByKeys(keys: string[], defaultValue?: number) {
    return keys.forEach(
      (key) =>
        // eslint-disable-next-line
        // @ts-ignore: not sure how to solve this issue
        (formFilteredData[key] =
          formRawData[key as keyof UnitRateType] ?? defaultValue)
    );
  }

  assignValuesByKeys(CORE_DAY_INPUT_KEYS);
  assignValuesByKeys(CORE_NIGHT_INPUT_KEYS);
  assignValuesByKeys(FLOAT_DAY_INPUT_KEYS);
  assignValuesByKeys(FLOAT_NIGHT_INPUT_KEYS);

  if (pricingMode === 'STATIC') {
    formFilteredData.displayAsLumpSum = formRawData.displayAsLumpSum ?? true;
    formFilteredData.staticPricingTiers = formRawData.staticPricingTiers || [];
  } else {
    assignValuesByKeys(DYNAMIC_RATES_INPUT_KEYS, 0);
  }

  return formFilteredData as UnitRateType;
}
